<template>
    <div :id="container" class="chart-wrapper" @contextmenu="contextMenuHandler" @click="containerClick">
        <component
            v-for="node in renderNodes"
            :is="node.component"
            :id="node.id"
            :key="node.id"
            :node="node"
            @connect="connectHanlder"
            @nodeClick="nodeClick"
        />
        <ContextMenu v-if="contextMenuData" :data="contextMenuData" @change="menuSelect" />
    </div>
</template>

<script>
import Flow from "./Flow.js";
import ContextMenu from "./ContextMemu.vue";

export default {
    components: {
        ContextMenu
    },
    props: ["nodeTypes", "containerId", "nodes", "connectors"],
    data() {
        return {
            renderNodes: [],
            flow: null,
            contextMenuData: null,
            scaleRate: 1
        };
    },
    computed: {
        container() {
            return this.containerId || "chartcontainer";
        }
    },
    mounted() {
        this.flow = new Flow({
            container: `#${this.container}`,
            connectorDbClick: connector => {
                this.deleteConnector(connector);
                this.flowChange();
                this.$emit("connectorChange", { type: "delete", connector });
            },
            connectOn: connector => {
                this.flowChange();
                this.$emit("connectorChange", { type: "add", connector });
            },
            draggerEnd: node => {
                this.flowChange();
                this.$emit("nodeChange", { type: "position", node });
            }
        });
        this.injectNodes();
        (this.nodes || this.connectors) && this.init(this.nodes, this.connectors);
    },
    destroyed() {
        this.destroy();
    },
    methods: {
        injectNodes() {
            const setPosition = node => {
                if (node.position) {
                    const elem = this.$el.querySelector(`#${node.id}`);
                    elem.style.left = `${node.position.x}px`;
                    elem.style.top = `${node.position.y}px`;
                }
            };
            const render = node => {
                this.renderNodes.push(node);
                return new Promise(resolve => {
                    this.$nextTick(() => {
                        setPosition(node);
                        resolve();
                    });
                });
            };
            const remove = ({ id }) => {
                const index = this.renderNodes.findIndex(n => n.id === id);
                this.renderNodes.splice(index, 1);
                return new Promise(resolve => {
                    this.$nextTick(() => {
                        resolve();
                    });
                });
            };
            const update = node => {
                const index = this.renderNodes.findIndex(n => n.id === node.id);
                this.renderNodes.splice(index, 1, node);
                return new Promise(resolve => {
                    this.$nextTick(() => {
                        setPosition(node);
                        resolve();
                    });
                });
            };

            this.nodeTypes.forEach(nt => {
                this.flow.injectNodeType(nt.nodeType, {
                    render,
                    update,
                    remove,
                    label: nt.label,
                    endpoints: nt.endpoints,
                    init: nt.init,
                    component: nt.component
                });
            });
        },
        async init(nodes, connectors) {
            await this.flow.init(nodes, connectors);
        },
        async updateNode(node) {
            await this.flow.updateNode(node);
        },
        async updateAll(nodes, connectors) {
            await this.flow.updateAll(nodes, connectors);
        },
        async addNode(node) {
            await this.flow.addNode(node);
            this.flowChange();
        },
        async deleteNode(node) {
            await this.flow.delNode(node);
            this.flowChange();
        },
        async setZoom(scaleRate) {
            this.scaleRate = scaleRate;
            await this.flow.setZoom(scaleRate);
        },
        reset() {
            this.flow.reset();
            this.renderNodes.splice(0);
        },
        destroy() {
            this.flow.destroy();
            this.renderNodes.splice(0);
            this.flow = null;
        },
        getExistNodeOptions(nodeId) {
            const nodeOptions = [];
            this.renderNodes.forEach(node => {
                if (node.id === nodeId || !node.name) {
                    return;
                }
                nodeOptions.push({
                    type: "node",
                    label: node.name,
                    value: node.id
                });
            });
            return nodeOptions.length > 0
                ? [
                      {
                          label: "选择已有节点",
                          value: "exsits",
                          children: nodeOptions
                      }
                  ]
                : [];
        },
        getNodeTypeOptions() {
            return this.flow.getNodeTypeList().map(nt => {
                return {
                    label: `新增【${nt.label}】节点`,
                    value: nt.nodeType,
                    type: "nodeType"
                };
            });
        },
        connectHanlder({ origEvent, node, sourceId, type }) {
            origEvent.stopPropagation();

            const options = [];
            if (type.indexOf("connect") !== -1) {
                options.push(...this.getExistNodeOptions(node.id));
            }
            if (type.indexOf("new") !== -1) {
                options.push(...this.getNodeTypeOptions());
            }
            this.contextMenuData = {
                type: type || ["new", "connect"],
                options,
                sourceId,
                sourceNode: node,
                ...this.getEventPosition(origEvent)
            };
        },
        contextMenuHandler(event) {
            event.preventDefault();
            const options = this.getNodeTypeOptions();
            this.contextMenuData = {
                type: ["new"],
                options,
                ...this.getEventPosition(event)
            };
        },
        async menuSelect(option) {
            if (!option.type) {
                return;
            }
            const { type, sourceNode, x, y, sourceId } = this.contextMenuData;
            this.contextMenuData = null;

            let newNodeId = null;
            if (option.type === "nodeType") {
                const nodeType = this.flow.nodeTypes[option.value];
                const initData = await nodeType.init();
                newNodeId = initData.id;
                let position = { x, y };
                if (sourceNode) {
                    const source = this.getElemById(sourceNode.id);
                    const sourceStyle = getComputedStyle(source);
                    // 新增这里暂时放到源节点左边的100px位置
                    position = { x: parseInt(sourceStyle.left, 10) + parseInt(sourceStyle.width, 10) + 100, y: parseInt(sourceStyle.top, 10) };
                }
                // 新增节点
                await this.addNode({ ...initData, nodeType: option.value, position });
            }

            const needConnect = type.indexOf("connect") != -1;
            if (!needConnect) {
                return;
            }
            setTimeout(() => {
                this.flow.connectLine(sourceId, newNodeId || option.value); // 新增时newNodeId, 引用时选择的节点的id（option.value）
            }, 300);
        },
        containerClick() {
            if (!this.contextMenuData) {
                return;
            }
            this.contextMenuData = null;
        },
        getParentRect() {
            return this.$el.getBoundingClientRect();
        },
        getEventPosition(event) {
            const parentRect = this.getParentRect();
            const box = this.$el;
            return {
                x: (event.clientX + box.scrollLeft - parentRect.x) / this.scaleRate,
                y: (event.clientY + box.scrollTop - parentRect.y) / this.scaleRate
            };
        },
        nodeClick(node) {
            this.$emit("nodeClick", node);
        },
        getElemById(id) {
            return this.$el.querySelector(`#${id}`);
        },
        // 删除连接线
        deleteConnector(connector) {
            this.flow.deleteConnection(connector);
        },
        // 流程图更新事件
        flowChange() {
            const { nodes, connectors } = this.flow.getFlowData();
            this.$emit("chartChange", { nodes, connectors });
        },
        getAll() {
            return this.flow.getFlowData();
        }
    }
};
</script>

<style scoped>
.chart-wrapper {
    position: relative;
    height: calc(100% - 10px);
}
</style>
